// sankeyLogger.js - Unified logging system for Sankey diagram troubleshooting
// Provides structured, filterable logging with correlation IDs for tracing

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

// Configuration - can be overridden at runtime
const config = {
  level: process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG,
  enableConsole: true,
  enableStorage: process.env.NODE_ENV !== 'production',
  maxStoredLogs: 500,
  contexts: {
    DATA_PREP: true,
    FILTER: true,
    LAYOUT: true,
    RENDER: true,
    INTERACTION: true,
    API: true,
    DRILLDOWN: true
  }
};

// In-memory log storage for debugging
const logStore = [];

// Generate correlation ID for request tracing
const generateCorrelationId = () => {
  return `sk-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
};

// Current correlation ID (set per filter operation or API request)
let currentCorrelationId = null;

// Format log entry for console output
const formatConsoleOutput = (entry) => {
  const prefix = {
    DEBUG: '\u{1F50D}',  // magnifying glass
    INFO: '\u{2139}\uFE0F',   // info
    WARN: '\u{26A0}\uFE0F',   // warning
    ERROR: '\u{274C}'   // cross
  }[entry.level] || '';

  const contextLabel = entry.context ? `[${entry.context}]` : '';
  const correlationLabel = entry.correlationId ? `(${entry.correlationId.slice(-8)})` : '';

  return `${prefix} ${entry.timestamp} ${contextLabel}${correlationLabel} ${entry.message}`;
};

// Core logging function
const log = (level, context, message, data = null, error = null) => {
  // Check if logging is enabled for this level and context
  if (LOG_LEVELS[level] < config.level) return;
  if (context && !config.contexts[context]) return;

  const entry = {
    id: generateCorrelationId(),
    timestamp: new Date().toISOString(),
    level,
    context,
    message,
    data: data ? JSON.parse(JSON.stringify(data)) : null,
    error: error ? {
      message: error.message,
      stack: error.stack,
      name: error.name
    } : null,
    correlationId: currentCorrelationId
  };

  // Store in memory
  if (config.enableStorage) {
    logStore.push(entry);
    if (logStore.length > config.maxStoredLogs) {
      logStore.shift();
    }
  }

  // Console output
  if (config.enableConsole) {
    const consoleOutput = formatConsoleOutput(entry);

    switch (level) {
      case 'DEBUG':
        console.debug(consoleOutput, data || '');
        break;
      case 'INFO':
        console.info(consoleOutput, data || '');
        break;
      case 'WARN':
        console.warn(consoleOutput, data || '');
        break;
      case 'ERROR':
        console.error(consoleOutput, data || '', error || '');
        break;
      default:
        console.log(consoleOutput, data || '');
    }
  }

  return entry;
};

// Public API
const SankeyLogger = {
  // Configure logging
  configure: (newConfig) => {
    Object.assign(config, newConfig);
  },

  // Set log level
  setLevel: (level) => {
    if (LOG_LEVELS[level] !== undefined) {
      config.level = LOG_LEVELS[level];
    }
  },

  // Enable/disable context
  enableContext: (context, enabled = true) => {
    config.contexts[context] = enabled;
  },

  // Start a new correlation scope
  startTrace: (label = 'operation') => {
    currentCorrelationId = generateCorrelationId();
    log('INFO', 'TRACE', `Starting trace: ${label}`, { correlationId: currentCorrelationId });
    return currentCorrelationId;
  },

  // End correlation scope
  endTrace: (summary = {}) => {
    if (currentCorrelationId) {
      log('INFO', 'TRACE', 'Ending trace', { correlationId: currentCorrelationId, ...summary });
      currentCorrelationId = null;
    }
  },

  // Context-specific loggers
  dataPrepare: {
    debug: (msg, data) => log('DEBUG', 'DATA_PREP', msg, data),
    info: (msg, data) => log('INFO', 'DATA_PREP', msg, data),
    warn: (msg, data) => log('WARN', 'DATA_PREP', msg, data),
    error: (msg, data, err) => log('ERROR', 'DATA_PREP', msg, data, err)
  },

  filter: {
    debug: (msg, data) => log('DEBUG', 'FILTER', msg, data),
    info: (msg, data) => log('INFO', 'FILTER', msg, data),
    warn: (msg, data) => log('WARN', 'FILTER', msg, data),
    error: (msg, data, err) => log('ERROR', 'FILTER', msg, data, err),

    // Specialized filter logging
    applied: (filterType, value, resultStats) => {
      log('INFO', 'FILTER', `Filter applied: ${filterType}`, {
        filterType,
        value,
        resultStats
      });
    },

    cleared: () => {
      log('INFO', 'FILTER', 'All filters cleared');
    }
  },

  layout: {
    debug: (msg, data) => log('DEBUG', 'LAYOUT', msg, data),
    info: (msg, data) => log('INFO', 'LAYOUT', msg, data),
    warn: (msg, data) => log('WARN', 'LAYOUT', msg, data),
    error: (msg, data, err) => log('ERROR', 'LAYOUT', msg, data, err),

    // Specialized layout logging
    calculated: (stats) => {
      log('INFO', 'LAYOUT', 'Layout calculated', stats);
    }
  },

  render: {
    debug: (msg, data) => log('DEBUG', 'RENDER', msg, data),
    info: (msg, data) => log('INFO', 'RENDER', msg, data),
    warn: (msg, data) => log('WARN', 'RENDER', msg, data),
    error: (msg, data, err) => log('ERROR', 'RENDER', msg, data, err),

    // Specialized render logging
    started: (nodeCount, linkCount) => {
      log('INFO', 'RENDER', 'Render started', { nodeCount, linkCount });
    },

    completed: (duration) => {
      log('INFO', 'RENDER', `Render completed in ${duration}ms`);
    }
  },

  interaction: {
    debug: (msg, data) => log('DEBUG', 'INTERACTION', msg, data),
    info: (msg, data) => log('INFO', 'INTERACTION', msg, data),
    warn: (msg, data) => log('WARN', 'INTERACTION', msg, data),
    error: (msg, data, err) => log('ERROR', 'INTERACTION', msg, data, err),

    // Specialized interaction logging
    nodeClicked: (nodeInfo) => {
      log('INFO', 'INTERACTION', 'Node clicked', {
        nodeType: nodeInfo.nodeType,
        nodeName: nodeInfo.nodeValue,
        layer: nodeInfo.layer
      });
    },

    linkClicked: (linkInfo) => {
      log('INFO', 'INTERACTION', 'Link clicked', linkInfo);
    },

    zoomed: (scale, translate) => {
      log('DEBUG', 'INTERACTION', 'Zoom changed', { scale, translate });
    }
  },

  api: {
    debug: (msg, data) => log('DEBUG', 'API', msg, data),
    info: (msg, data) => log('INFO', 'API', msg, data),
    warn: (msg, data) => log('WARN', 'API', msg, data),
    error: (msg, data, err) => log('ERROR', 'API', msg, data, err),

    // Specialized API logging
    request: (endpoint, params) => {
      log('INFO', 'API', `Request: ${endpoint}`, { params });
    },

    response: (endpoint, status, data) => {
      log('INFO', 'API', `Response: ${endpoint} (${status})`, {
        nodeCount: data?.nodes?.length,
        linkCount: data?.links?.length,
        totalFlow: data?.total_flow
      });
    },

    failed: (endpoint, error) => {
      log('ERROR', 'API', `Request failed: ${endpoint}`, null, error);
    }
  },

  drilldown: {
    debug: (msg, data) => log('DEBUG', 'DRILLDOWN', msg, data),
    info: (msg, data) => log('INFO', 'DRILLDOWN', msg, data),
    warn: (msg, data) => log('WARN', 'DRILLDOWN', msg, data),
    error: (msg, data, err) => log('ERROR', 'DRILLDOWN', msg, data, err),

    // Specialized drilldown logging
    opened: (nodeInfo) => {
      log('INFO', 'DRILLDOWN', 'Drilldown modal opened', nodeInfo);
    },

    closed: () => {
      log('INFO', 'DRILLDOWN', 'Drilldown modal closed');
    },

    dataLoaded: (rowCount) => {
      log('INFO', 'DRILLDOWN', `Loaded ${rowCount} rows`);
    }
  },

  // Get stored logs
  getLogs: (options = {}) => {
    let result = [...logStore];

    if (options.level) {
      const minLevel = LOG_LEVELS[options.level] || 0;
      result = result.filter(e => LOG_LEVELS[e.level] >= minLevel);
    }

    if (options.context) {
      result = result.filter(e => e.context === options.context);
    }

    if (options.correlationId) {
      result = result.filter(e => e.correlationId === options.correlationId);
    }

    if (options.since) {
      const sinceTime = new Date(options.since).getTime();
      result = result.filter(e => new Date(e.timestamp).getTime() >= sinceTime);
    }

    if (options.limit) {
      result = result.slice(-options.limit);
    }

    return result;
  },

  // Export logs for debugging
  exportLogs: () => {
    const blob = new Blob(
      [JSON.stringify(logStore, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sankey-logs-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Clear stored logs
  clearLogs: () => {
    logStore.length = 0;
  },

  // Debug helper - dump current state
  dumpState: (label, state) => {
    log('DEBUG', 'STATE', `State dump: ${label}`, {
      ...state,
      _dumpTime: new Date().toISOString()
    });
  },

  // Performance timing helper
  time: (label) => {
    const startTime = performance.now();
    return {
      end: (additionalData = {}) => {
        const duration = Math.round(performance.now() - startTime);
        log('INFO', 'PERF', `${label}: ${duration}ms`, { duration, ...additionalData });
        return duration;
      }
    };
  }
};

// Make available globally for debugging in console
if (typeof window !== 'undefined') {
  window.SankeyLogger = SankeyLogger;
}

export default SankeyLogger;
export { LOG_LEVELS };
